package com.evoapps.evoshift.data.repository

import com.evoapps.evoshift.data.dto.roster.AbsenceDto
import com.evoapps.evoshift.data.dto.roster.ShiftDto
import com.evoapps.evoshift.data.mapper.AbsenceMapper
import com.evoapps.evoshift.data.mapper.ShiftMapper
import com.evoapps.evoshift.domain.model.Absence
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.repository.AbsenceRepository
import com.evoapps.evoshift.data.remote.isPermissionDenied
import com.evoapps.evoshift.data.remote.safeSnapshots
import com.evoapps.evoshift.data.remote.AppFirestore
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.map

class AbsenceRepositoryImpl(
    private val firestore: AppFirestore,
    private val absenceMapper: AbsenceMapper,
    private val shiftMapper: ShiftMapper
) : AbsenceRepository {

    override suspend fun createAbsence(absence: Absence): Result<String> {
        return try {
            val dto = absenceMapper.toDto(absence)
            // W bibliotece KMP 'add' zwraca DocumentReference
            val docRef = firestore.collection("absences").add(dto)
            Result.success(docRef.id)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun getAbsencesForRoster(
        workplaceId: String,
        start: String,
        end: String
    ): Result<List<Absence>> {
        return try {
            // SH-197: the workplace's absences only. The rules let a manager read the absences of
            // their workplace (isManager(resource.data.workplaceId)); a query over the whole
            // collection cannot be proven by them and was refused every time, which the callers
            // (copying days, auto-assign, capacity, the month's answers) took as "nobody is away".
            // Absences without a workplace (all of a person's workplaces) are their person's own.
            val snapshot = firestore.collection("absences")
                .where { "workplaceId" equalTo workplaceId }
                .get()

            val absences = snapshot.documents
                .mapNotNull { doc -> runCatching { doc.data<AbsenceDto>() to doc.id }.getOrNull() }
                .filter { (dto, _) ->
                    // 1. Sprawdź czy dotyczy tego Workplace LUB jest globalna (null)
                    val isRelevantWorkplace =
                        dto.workplaceId == workplaceId || dto.workplaceId == null

                    // 2. Sprawdź czy daty się nakładają (Overlap Logic)
                    // (Start nieobecności jest przed końcem okna) ORAZ (Koniec nieobecności jest po starcie okna)
                    val isDateOverlapping = dto.startDate <= end && dto.endDate >= start

                    isRelevantWorkplace && isDateOverlapping
                }
                .map { (dto, id) -> absenceMapper.toDomain(dto, id) }
                .sortedBy { it.startDate } // Sortowanie dla UI

            Result.success(absences)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun getOverlappingShifts(
        userId: String,
        start: String,
        end: String
    ): Result<List<Shift>> {
        return try {
            // Tutaj zapytanie jest proste i indeksowalne
            val snapshot = firestore.collection("shifts")
                .where { "userId" equalTo userId }
                .where { "date" greaterThanOrEqualTo start }
                .where { "date" lessThanOrEqualTo end }
                .get()

            val shifts = snapshot.documents
                .map { shiftMapper.toDomain(it.data<ShiftDto>()) }

            Result.success(shifts)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun getAbsencesForUser(userId: String): Result<List<Absence>> {
        return try {
            val snapshot = firestore.collection("absences")
                .where { "userId" equalTo userId }
                .get()
            val absences = snapshot.documents
                .decoded()
                .sortedByDescending { it.startDate }
            Result.success(absences)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    /**
     * SH-224: one query for the timeline: the person and the workplace (what the rules check,
     * isUser or isManager of that workplace) ending on or after [from]; the start is compared
     * here (a second range field would need a multi-field inequality index). firestore.indexes.json
     * has the composite index (userId, workplaceId, endDate).
     */
    override suspend fun getAbsencesOfUserBetween(userId: String, workplaceId: String, from: String, to: String): Result<List<Absence>> =
        runCatching {
            firestore.collection("absences")
                .where { "userId" equalTo userId }
                .where { "workplaceId" equalTo workplaceId }
                .where { "endDate" greaterThanOrEqualTo from }
                .get()
                .documents
                .decoded()
                .filter { it.startDate <= to }
                .sortedBy { it.startDate }
        }

    override suspend fun getAbsencesForWorkplace(workplaceId: String): Result<List<Absence>> {
        return try {
            val snapshot = firestore.collection("absences")
                .where { "workplaceId" equalTo workplaceId }
                .get()
            val absences = snapshot.documents
                .decoded()
                .sortedByDescending { it.startDate }
            Result.success(absences)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    /** SH-164: the same query as [getAbsencesForWorkplace], live (the pending-request badges). */
    override fun observeAbsencesForWorkplace(workplaceId: String): Flow<List<Absence>> =
        firestore.collection("absences")
            .where { "workplaceId" equalTo workplaceId }
            .safeSnapshots
            .map { snapshot ->
                snapshot.documents
                    .decoded()
                    .sortedByDescending { it.startDate }
            }
            // Not a manager here (any more): nothing to decide.
            .catch { e -> if (e.isPermissionDenied()) emit(emptyList()) else throw e }
            // Snapshots fire from the cache and again from the server with the same data.
            .distinctUntilChanged()

    /**
     * SH-223: each document on its own: one this version cannot read (a later version's shape) is
     * left out instead of failing the whole list, which is what broke the lists of older apps.
     */
    private fun List<dev.gitlive.firebase.firestore.DocumentSnapshot>.decoded(): List<Absence> =
        mapNotNull { doc -> runCatching { absenceMapper.toDomain(doc.data<AbsenceDto>(), doc.id) }.getOrNull() }

    override suspend fun updateAbsence(absence: Absence): Result<Unit> {
        return try {
            val dto = absenceMapper.toDto(absence)
            firestore.collection("absences").document(absence.id).set(dto)
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun deleteAbsence(absenceId: String): Result<Unit> {
        return try {
            firestore.collection("absences").document(absenceId).delete()
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }
}
